import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { apiRequest, streamApiRequest, fileToBase64WithMetadata } from '../services/api';
import { useAuth } from './AuthContext';

const ChatContext = createContext();

export const useChat = () => useContext(ChatContext);

export const ChatProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const lastMessageRef = useRef(null);
  const abortControllerRef = useRef(null);
  const prevCurrentSessionIdRef = useRef(currentSessionId);
  const currentSessionIdRef = useRef(currentSessionId); // Ref to track current session ID

  // Update the currentSessionIdRef whenever currentSessionId changes
  useEffect(() => {
    currentSessionIdRef.current = currentSessionId;
  }, [currentSessionId]);
  
  // --- Define loadSessions using useCallback --- 
  const loadSessions = useCallback(async () => {
    setError(null);
    try {
      const sessionsData = await apiRequest('/api/chat/sessions', 'GET');
      setSessions(sessionsData);
      if (sessionsData.length > 0 && !currentSessionIdRef.current) {
        setCurrentSessionId(sessionsData[0].id);
      }
    } catch (err) {
      setError(err.message || 'Failed to load sessions');
      console.error('Failed to load sessions:', err);
    }
  }, []); // Dependencies for useCallback (empty if no external state used directly in logic, or currentSessionIdRef.current is stable)

  // Load sessions when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadSessions();
    } else {
      setSessions([]);
      setCurrentSessionId(null);
      setMessages([]);
    }
  }, [isAuthenticated, loadSessions]); // Add loadSessions to dependency array

  // Load messages when currentSessionId changes
  useEffect(() => {
    if (currentSessionId) {
      // Skip loading messages if we just created a session with a message
      if (window._skipNextLoadMessages) {
        window._skipNextLoadMessages = false;
        return;
      }
      
      // Always load messages when switching to a session to ensure we have the latest
      const requestedSessionId = currentSessionId;
      setLoading(true);
      
      loadMessages(requestedSessionId)
        .catch(err => {
          console.error("Error loading messages in useEffect:", err);
          setError(err.message || 'Failed to load messages during session switch');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // When currentSessionId becomes null (new chat), always clear messages
      setMessages([]);
      setLoading(false);
    }
    prevCurrentSessionIdRef.current = currentSessionId;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionId]);

  // --- Original loadMessages function (not wrapped in useCallback as it's called directly with sessionId) ---
  const loadMessages = async (sessionId) => {
    setError(null);
    try {
      const messagesData = await apiRequest(`/api/chat/sessions/${sessionId}/messages`, 'GET');
      
      if (messagesData && messagesData.length === 0 && messages.length > 0 && 
          messages[0].sender === 'user' && currentSessionIdRef.current === sessionId) {
        return; // Keep existing messages
      }
      
      // Only update messages if we're still on the same session
      if (currentSessionIdRef.current === sessionId) {
        // Format timestamps and preserve images before setting them into state
        const formattedMessages = (messagesData || []).map(msg => ({
          ...msg,
          timestamp: formatTimestamp(msg.timestamp),
          images: msg.images || undefined // Preserve images from database
        }));
        setMessages(formattedMessages);
      }
    } catch (err) {
      setError(err.message || 'Failed to load messages');
      console.error('Failed to load messages:', err);
    }
  };

  // Create a new chat session
  const createSession = async (initialMessage = '', classSectionId = null) => {
    setError(null);
    try {
      const sessionTitle = generateSessionTitle(initialMessage);
      const body = { title: sessionTitle };
      if (classSectionId) {
        body.class_section_id = classSectionId;
      }
      const sessionData = await apiRequest('/api/chat/sessions', 'POST', body);

      if (!sessionData || !sessionData.id) {
        throw new Error('Failed to create session: Invalid data from API.');
      }

      setSessions(prevSessions => {
        const updatedSessions = [sessionData, ...prevSessions.filter(s => s.id !== sessionData.id)];
        return updatedSessions;
      });

      setCurrentSessionId(sessionData.id);
      return sessionData;
    } catch (err) {
      setError(err.message || 'Failed to create new session');
      console.error('Failed to create new session:', err);
      throw err;
    }
  };

  // Format timestamp to a more readable format
  const formatTimestamp = (timestampInput) => {
    if (!timestampInput) return '';

    let dateObj;
    if (timestampInput instanceof Date) {
      dateObj = timestampInput;
    } else if (typeof timestampInput === 'string') {
      // Attempt to parse it as an ISO string primarily
      dateObj = new Date(timestampInput);
      // Check if parsing failed (e.g., it was already a simple time string like "1:46 PM")
      // getTime() will be NaN for invalid dates.
      if (isNaN(dateObj.getTime())) {
        // If parsing failed, it might be an already formatted string. Return as is.
        // Or, decide on a more robust error handling or re-parsing strategy if needed.
        return timestampInput; 
      }
    } else {
      // Not a Date object or a string, return empty or handle as an error
      return ''; 
    }

    return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Clear error
  const clearError = () => setError(null);

  // Stop an in-flight stream
  const stopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  // Retry last failed message
  const retryLastMessage = async () => {
    if (!lastMessageRef.current) return;
    const { message, images } = lastMessageRef.current;
    await sendMessage(message, images);
  };

  // Send a message
  const sendMessage = async (message, images = [], explicitSessionId = null) => {
    const sessionIdAtStart = explicitSessionId || currentSessionId;
    let newlyCreatedSession = false;
    let sessionIdToUse = sessionIdAtStart;

    lastMessageRef.current = { message, images };
    setError(null);

    try {
      // Step 1: Create a new session if needed
      if (!sessionIdToUse) {
        const newSession = await createSession(message || "[Images]");
        if (!newSession || !newSession.id) {
          throw new Error('Failed to create session for sending message');
        }

        sessionIdToUse = newSession.id;
        newlyCreatedSession = true;

        // Flag to prevent loadMessages from overwriting our pending message
        window._skipNextLoadMessages = true;

        // Important: Update both state and ref
        setCurrentSessionId(sessionIdToUse);
        currentSessionIdRef.current = sessionIdToUse;
      }

      // Step 1.5: Convert images to base64 for database storage
      let processedImages = [];
      if (images && images.length > 0) {
        try {
          processedImages = await Promise.all(
            images.map(file => fileToBase64WithMetadata(file))
          );

          // Add preview URLs for optimistic UI
          processedImages = processedImages.map((img, idx) => ({
            ...img,
            mime_type: images[idx].type,
            // Create temporary object URL for preview
            previewUrl: URL.createObjectURL(images[idx])
          }));
        } catch (err) {
          console.error('Failed to process images:', err);
          throw new Error('Failed to process images');
        }
      }

      // Step 2: Create user message object
      const userMessage = {
        id: `temp-${Date.now()}`,
        sender: 'user',
        message: message || (processedImages.length > 0 ? "[Images]" : ""),
        timestamp: formatTimestamp(new Date().toISOString()),
        images: processedImages.length > 0 ? processedImages.map(img => ({
          base64: img.base64, // Use actual base64 data
          mime_type: img.mime_type,
          filename: img.filename
        })) : undefined
      };

      // Step 3: Update UI with user message immediately 
      if (newlyCreatedSession) {
        // For new sessions, start with just the user message
        setMessages([userMessage]);
      } else {
        // For existing sessions, append to current messages
        setMessages(prevMessages => [...prevMessages, userMessage]);
      }

      // Step 4: Set loading to true AFTER showing user message
      // This ensures we first see user message, then loading indicator
      setLoading(true);
      
      // Step 5: Stream AI response
      const aiMessageId = `ai-${Date.now()}`;
      const aiTimestamp = formatTimestamp(new Date().toISOString());
      
      let bufferedText = '';
      let revealIndex = 0;
      let streamingDone = false;
      let aiMessageRendered = false; // Tracks if the AI message bubble is in the UI

      const revealLoop = () => {
        if (currentSessionIdRef.current !== sessionIdToUse) return; // Stops if session changed

        if (aiMessageRendered) { // Only try to update if the message bubble exists
          if (revealIndex < bufferedText.length) {
            // Advance ~12 characters per frame with word-boundary snapping
            let nextIndex = Math.min(revealIndex + 12, bufferedText.length);
            if (nextIndex < bufferedText.length) {
              const nextSpace = bufferedText.indexOf(' ', nextIndex);
              const nextNewline = bufferedText.indexOf('\n', nextIndex);
              let boundary = bufferedText.length;
              if (nextSpace !== -1 && nextSpace < nextIndex + 20) boundary = Math.min(boundary, nextSpace + 1);
              if (nextNewline !== -1 && nextNewline < nextIndex + 20) boundary = Math.min(boundary, nextNewline + 1);
              if (boundary < bufferedText.length) nextIndex = boundary;
            }
            setMessages(prevMessages =>
              prevMessages.map(msg =>
                msg.id === aiMessageId
                  ? { ...msg, message: bufferedText.slice(0, nextIndex) }
                  : msg
              )
            );
            revealIndex = nextIndex;
          }
          // Continue loop if message is rendered AND (stream not done OR not all text revealed)
          if (!streamingDone || revealIndex < bufferedText.length) {
            requestAnimationFrame(revealLoop);
          }
        } else if (!streamingDone) {
          // If message not yet rendered AND stream not done, keep polling
          requestAnimationFrame(revealLoop);
        }
        // If !aiMessageRendered AND streamingDone, loop stops.
      };
      // Start the reveal loop.
      requestAnimationFrame(revealLoop);

      try {
        // Build request body with message and images
        const requestBody = {
          message: message || ""
        };

        // Add images if present (use actual base64, not preview URLs)
        if (processedImages.length > 0) {
          requestBody.images = processedImages.map(img => ({
            base64: img.base64,
            filename: img.filename
          }));
        }

        // Create AbortController for stop functionality
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        setIsStreaming(true);

        for await (const chunk of streamApiRequest(
          `/api/chat/sessions/${sessionIdToUse}/messages/stream`,
          requestBody,
          abortController.signal
        )) {
          // console.log(`[ChatContext] Received chunk: ${JSON.stringify(chunk)}`); // REMOVED

          // Process chunk if it's not empty, OR if message already rendered (to allow multi-part messages with empty separators)
          if ((chunk && chunk.trim() !== "") || aiMessageRendered) {
            // Check if chunk is a session_title event (JSON with session_title key)
            if (typeof chunk === 'object' && chunk.session_title) {
              // Update session title from backend auto-generation
              const newTitle = chunk.session_title;
              setSessions(prevSessions =>
                prevSessions.map(s =>
                  s.id === sessionIdToUse ? { ...s, title: newTitle } : s
                )
              );
              continue;
            }
            if (!aiMessageRendered) {
              // This is the first non-empty chunk
              setMessages(prevMessages => [
                ...prevMessages,
                { id: aiMessageId, sender: 'ai', message: '', timestamp: aiTimestamp }
              ]);
              setLoading(false); // Turn off loading indicator as we have content
              aiMessageRendered = true; // Mark that the bubble is in the UI
            }
            bufferedText += chunk; // Append chunk to buffer
          } else {
            // First chunk(s) are empty, and message not yet rendered.
            // Loading indicator is still active. Loop continues, revealLoop will re-poll.
            // console.log(`[ChatContext] Received empty initial chunk. Waiting for content.`); // REMOVED
          }
        }
        // console.log(`[ChatContext] Streaming completed for session ${sessionIdToUse}. Final bufferedText: ${JSON.stringify(bufferedText)}`); // REMOVED
        
      } catch (streamError) {
        // Don't show error for user-initiated abort
        if (streamError.name !== 'AbortError') {
          console.error('Streaming error:', streamError);
          setError(streamError.message || 'Stream error');
        }
        // Loading state will be handled by finally
      } finally {
        streamingDone = true; // Signal revealLoop that streaming has finished
        setIsStreaming(false);
        abortControllerRef.current = null;

        // If loading is still true at this point (e.g. all chunks were empty or error before first content)
        // ensure it's turned off.
        setLoading(prevLoading => {
          if (prevLoading) {
            return false;
          }
          return prevLoading;
        });

        // If the stream finished and no message was ever rendered (all chunks empty)
        if (!aiMessageRendered && !error) {
            // Stream completed but no content was rendered
        }
      }

      // Step 9: Update session title for new sessions (fallback if backend didn't auto-title)
      if (newlyCreatedSession) {
        // Check if backend already set a title via the stream
        const currentSessionState = sessions.find(s => s.id === sessionIdToUse);
        if (!currentSessionState || currentSessionState.title === 'New Chat') {
          const updatedTitle = generateSessionTitle(message);
          try {
            await apiRequest(`/api/chat/sessions/${sessionIdToUse}`, 'PUT', { title: updatedTitle });
            setSessions(prevSessions =>
              prevSessions.map(s =>
                s.id === sessionIdToUse ? { ...s, title: updatedTitle } : s
              )
            );
          } catch (titleUpdateError) {
            console.error(`Failed to update title for session ${sessionIdToUse}. Error:`, titleUpdateError);
          }
        }
      }

      // Completed streaming and title update
      return;
    } catch (err) {
      setError(err.message || 'Failed to send message');
      console.error('Failed to send message:', err);
      // Set loading to false in case of error
      setLoading(false);
    }
  };

  // Delete a session
  const deleteSession = async (sessionId) => {
    setError(null);
    try {
      await apiRequest(`/api/chat/sessions/${sessionId}`, 'DELETE');
      
      // Remove from local state
      setSessions(prevSessions => prevSessions.filter(session => session.id !== sessionId));
      
      // If the deleted session is the current one, clear current session
      if (sessionId === currentSessionId) {
        setCurrentSessionId(null);
        setMessages([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to delete session');
      console.error('Failed to delete session:', err);
    }
  };

  // Clear all sessions
  const clearAllSessions = async () => {
    setError(null);
    try {
      await apiRequest('/api/chat/sessions', 'DELETE');
      setSessions([]);
      setCurrentSessionId(null);
      setMessages([]);
    } catch (err) {
      setError(err.message || 'Failed to clear all sessions');
      console.error('Failed to clear all sessions:', err);
    }
  };

  // Generate a title based on the first message
  const generateSessionTitle = (message) => {
    if (!message) return "New Chat";
    // Take the first few words (up to 5) or 30 characters
    const words = message.split(' ');
    const title = words.slice(0, 5).join(' ');
    if (title.length > 30) {
      return title.substring(0, 27) + '...';
    }
    return title;
  };

  // Handle clicking on an example from the welcome screen
  const handleExampleClick = async (exampleText) => {
    setError(null); // Reset error state
    try {
      // Let sendMessage handle session creation if needed.
      // sendMessage will internally check if currentSessionId is null
      // and call createSession if necessary.
      await sendMessage(exampleText);
    } catch (err) {
      // If sendMessage throws (e.g., session creation fails critically),
      // catch it here. Errors during the message send itself are
      // handled within sendMessage.
      setError(err.message || 'Failed to start new chat with example');
      console.error('Failed to start new chat with example:', err);
    }
  };

  // Derive current session metadata
  const currentSession = sessions.find(s => s.id === currentSessionId) || null;
  const currentSessionMeta = currentSession ? {
    id: currentSession.id,
    title: currentSession.title,
    class_section_id: currentSession.class_section_id,
    message_count: messages.length,
  } : null;

  // Set feedback on an AI message
  const setMessageFeedback = async (sessionId, messageId, feedback) => {
    try {
      await apiRequest(
        `/api/chat/sessions/${sessionId}/messages/${messageId}/feedback`,
        'PUT',
        { feedback }
      );
      // Update local state
      setMessages(prevMessages =>
        prevMessages.map(msg =>
          msg.id === messageId ? { ...msg, feedback } : msg
        )
      );
    } catch (err) {
      console.error('Failed to set feedback:', err);
    }
  };

  // Add messages from voice transcriptions in real-time
  const addVoiceMessages = (newMessages) => {
    if (!newMessages || newMessages.length === 0) return;
    const tagged = newMessages.map(m => ({ ...m, isVoiceNew: true }));
    setMessages(prev => [...prev, ...tagged]);
  };

  const value = {
    sessions,
    currentSessionId,
    currentSessionMeta,
    messages,
    loading,
    isStreaming,
    error,
    setCurrentSessionId,
    sendMessage,
    createSession,
    stopStreaming,
    deleteSession,
    clearAllSessions,
    clearError,
    retryLastMessage,
    handleExampleClick,
    setMessageFeedback,
    addVoiceMessages
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContext; 